import { ArrowRight, CalendarDays, Check, ClipboardList, Clock3, CreditCard, FileText, MapPin, MessageSquare, Smartphone, UsersRound } from 'lucide-react'
import {
  getProfessionalTrialUrl,
  SharedFooter,
  SharedNav,
  StartFreeOrUpgradeLabel,
  TrialFinePrint,
  useMeta,
} from '../shared'
import { getProfessionCategoryContent, professionPageHeading, professionWorkNounFor, usesPracticeManagementLanguage } from './categoryContent.js'
import { pluralProfessionName, professionNameInSentence } from './professionCopy.js'
import { getProfessionHeroImage } from './professionImages.js'
import { getProfessionNuance } from './professionNuances.js'
import { getProfessionPresentation } from './professionPresentation.js'
import { getProfessionWorkflow } from './professionWorkflow.js'
import { PROFESSION_CHILD_ROUTE_META, PROFESSION_ROUTE_META } from './professionSeo.js'
import './professionCluster.css'

type Profession = {
  id: string
  name: string
  slug: string
  category: string
  categorySlug: string
  canonicalHref: string
  bookingPattern: string
  serviceSetting: string
}

type ClusterImage = { src: string; alt: string; width?: number; height?: number; kind?: string; gallery?: Array<{ src: string; alt: string }>; scenes?: Record<string, { src: string; alt: string }> }

const productVisuals = {
  calendar: '/public-assets/homepage-platform/offlico-professional-app-iphone-calendar.webp',
  records: '/public-assets/features/feature-cells/client-records/offlico-client-record-contact-address-details.webp',
  reminders: '/public-assets/features/feature-cells/reminders/offlico-reminders-response-inbox.webp',
  payment: '/public-assets/features/feature-cells/tap-to-pay/offlico-tap-to-pay-no-card-reader.webp',
}

const childLabels: Record<string, string> = {
  'admin-overload': 'Admin overload',
  'lead-generation': 'Managing enquiries',
  'online-booking': 'Online booking',
  'appointment-reminders': 'Appointment reminders',
  'client-records': 'Client records',
  'practice-management-software': 'Business management software',
}

function childLabel(profession: Profession, slug: string) {
  return slug === 'practice-management-software' && usesPracticeManagementLanguage(profession)
    ? 'Practice management software'
    : childLabels[slug]
}

const childHeadings: Record<string, (rolePlural: string) => string> = {
  'admin-overload': (rolePlural) => `${rolePlural.charAt(0).toLocaleUpperCase('en-GB') + rolePlural.slice(1)}: spending too much time on admin?`,
  'lead-generation': (rolePlural) => `Suitable enquiries for ${rolePlural}`,
  'online-booking': (rolePlural) => `Online booking for ${rolePlural}`,
  'appointment-reminders': (rolePlural) => `Booking reminders for ${rolePlural}`,
  'client-records': (rolePlural) => `Client records for ${rolePlural}`,
  'practice-management-software': (rolePlural) => `Business management for ${rolePlural}`,
}

const childEyebrows: Record<string, string> = {
  'admin-overload': 'Make the day feel lighter',
  'lead-generation': 'Make room for the right work',
  'online-booking': 'Booking that works while you work',
  'appointment-reminders': 'Before the next booking',
  'client-records': 'Know the contact before work begins',
  'practice-management-software': 'How Offlico works',
}

const childIntroHeadings: Record<string, string> = {
  'admin-overload': 'Keep the working day in one place.',
  'lead-generation': 'From your own enquiry channels to suitable work.',
  'online-booking': 'Bookings without interrupting the work.',
  'appointment-reminders': 'Before the booked work begins.',
  'client-records': 'Know the contact and the work before you begin.',
  'practice-management-software': 'One booking. Every part connected.',
}

const childStepHeadings: Record<string, string> = {
  'admin-overload': 'From booked work to finished admin',
  'lead-generation': 'From enquiry to suitable work',
  'online-booking': 'From service page to booking request',
  'appointment-reminders': 'From booking to a prepared working day',
  'client-records': 'From contact to useful business history',
  'practice-management-software': 'One booking, every part connected',
}

const childBenefitContent: Record<string, { heading: string; body: (role: string, workNoun: string) => string; points: string[] }> = {
  'admin-overload': {
    heading: 'Leave less admin for the evening.',
    body: (role, workNoun) => `Keep the everyday business details of ${roleWork(role, workNoun)} beside the booking, instead of reconstructing the day from messages and loose notes.`,
    points: ['One view of booked work', 'The right contact to hand', 'Reminders linked to the diary', 'Payment follow-up in view'],
  },
  'lead-generation': {
    heading: 'Choose suitable work, not just more work.',
    body: (role) => `An enquiry about ${role} work is useful when the service, location and timing fit. Use your own enquiry channels, then organise accepted work in Offlico.`,
    points: ['Work that matches the service', 'A workable location', 'Diary time you can genuinely offer', 'A clear follow-up when it fits'],
  },
  'online-booking': {
    heading: 'Let a booking begin while you are busy.',
    body: (role) => `Give people a clear way to request ${role} work without interrupting the work already in front of you.`,
    points: ['Clear service information', 'Useful contact and location details', 'Availability that fits the diary', 'The next booking in view'],
  },
  'appointment-reminders': {
    heading: 'Make the next booking easier to remember.',
    body: (role) => `For ${role} work, a forgotten booking can leave a gap in the day. A timely reminder gives both sides a chance to prepare.`,
    points: ['Booking details in one place', 'A reminder before the agreed time', 'The right location before travel', 'Follow-up visible in the diary'],
  },
  'client-records': {
    heading: 'Remember the person, not just the slot.',
    body: (role) => `Bring the contact and previous ${role} bookings back into view before the next piece of work, while specialist records stay in the systems your profession requires.`,
    points: ['Correct contact and address', 'Previous ordinary business bookings', 'Useful logistics and notes', 'Invoice and payment status'],
  },
  'practice-management-software': {
    heading: 'The business side, connected.',
    body: (role) => `Keep ${role} scheduling, contacts, reminders and payments together, without claiming to replace specialist tools.`,
    points: ['Plan the work', 'Keep the right contact', 'Keep reminders on track', 'Finish payment follow-up'],
  },
}

const childIntros: Record<string, (role: string, workNoun: string) => string> = {
  'admin-overload': (role) =>
    `Bookings, messages and follow-up can pile up while you are busy with ${role} work. They still need attention after the work is done.`,
  'lead-generation': (role) =>
    `Offlico local customer requests are not currently available for ${role} work. Use your own enquiry channels, then organise suitable accepted work in Offlico.`,
  'online-booking': (role) =>
    `Let people request ${role} work while you are busy, with the contact and location details ready to review.`,
  'appointment-reminders': () =>
    'A timely reminder gives both sides a chance to check the agreed time and location before the work begins.',
  'client-records': (role) =>
    `Keep ${role} contacts, booking history and everyday business notes together, ready before the next piece of work.`,
  'practice-management-software': (role, workNoun) =>
    `Bring ${roleWork(role, workNoun)}, contacts, reminders and payments into one connected business workflow.`,
}

function bookingFirstStep(bookingPattern: string) {
  if (/quote|survey|assessment|consultation|project|installation|contract|assignment/i.test(bookingPattern)) {
    return 'If the work needs a first conversation, assessment or quote, make that step clear instead of implying a time slot settles the scope of the work.'
  }
  if (/event|performance|production|tour|delivery|journey|move|collection/i.test(bookingPattern)) {
    return 'For work tied to a venue, route or collection, confirm the timing and location details before you commit the day.'
  }
  return 'Even when an available time looks suitable, check that the requested service, place and timing fit the work you actually offer.'
}

function childPracticalCopy(slug: string, role: string, bookingPattern: string) {
  const copy: Record<string, string> = {
    'admin-overload': `For ${role} work, a changed time, a different location or a missing contact can interrupt a day that was otherwise planned. Keep those ordinary details beside the booking so you can see what needs attention before the next piece of work begins.`,
    'lead-generation': `An enquiry is not yet a booking. Find out what is needed, where or how it would happen and whether the timing is realistic before you commit to ${role} work. It is fine to ask a useful follow-up question or decline work that does not fit.`,
    'online-booking': `A booking request is a starting point, not a promise that every request for ${role} work can be confirmed without discussion. ${bookingFirstStep(bookingPattern)}`,
    'appointment-reminders': `A reminder works best when the booking is already clear. Agree the time, place and practical arrangements for ${role} work first, then let the reminder help both sides recognise the appointment and prepare for it.`,
    'client-records': `An ordinary business record answers practical questions: whom to contact, where the work was booked and what follow-up is due. If your work needs specialist case notes, keep them in the appropriate system rather than treating ordinary booking notes as a substitute.`,
    'practice-management-software': `Choose the parts of the working day you want to connect. A booking for ${role} work may lead to travel, an invoice or a repeat visit, but that does not mean the same app must replace every tool used to perform or document the work itself.`,
  }
  return copy[slug]
}

const childOutcomeCopy: Record<string, string> = {
  'admin-overload': 'When the plan changes, the next booking and its contact details are easier to find in one place than across a string of messages. You can spend less time reconstructing what was agreed and more time preparing for the work ahead.',
  'lead-generation': 'If the request is suitable, move the agreed work into the diary with the right contact and location. If it is not, a clear reply is more useful than filling the calendar with work you cannot take on.',
  'online-booking': 'If you need more information, follow up before committing to the full work. For a simpler service, the request still gives you a clear contact and proposed time to work from.',
  'appointment-reminders': 'The automatic reminder helps protect an agreed booking, but it cannot correct a wrong address or work that was never clearly agreed. Check those details when the appointment is first added.',
  'client-records': 'The next piece of work is easier to prepare for when the previous ordinary booking and next business action are visible. Check whether the contact, location or agreed time has changed before you start.',
  'practice-management-software': 'Start with the diary and contact details, then use the reminder and payment steps that fit your work. Each ordinary business step should give useful context to the next one.',
}

function roleName(profession: Profession) {
  return professionNameInSentence(profession.name)
}

function rolePlural(profession: Profession) {
  return pluralProfessionName(profession.name)
}

function roleWork(role: string, workNoun: string) {
  return workNoun === 'client work' ? `${role} work` : `${role} ${workNoun}`
}

function intentScene(image: ClusterImage, kind: string, fallbackIndex: number) {
  return image.scenes?.[`${kind}-support`] || image.scenes?.[`${kind}-hero`] || image.gallery?.[fallbackIndex] || image
}

function professionRoute(profession: Profession, suffix = '') {
  return `${profession.canonicalHref}${suffix ? `${suffix}/` : ''}`
}

function TrialAction({ className = '' }: { className?: string }) {
  return (
    <a className={`ocp-primary ${className}`.trim()} href={getProfessionalTrialUrl()} data-cta-kind="trial">
      <StartFreeOrUpgradeLabel /><ArrowRight aria-hidden="true" size={18} />
    </a>
  )
}

function PageBreadcrumb({ profession, suffix }: { profession: Profession; suffix: string }) {
  return (
    <nav className="ocp-breadcrumb" aria-label="Breadcrumb">
      <a href="/">Home</a><span aria-hidden="true">/</span>
      {suffix ? <a href={professionRoute(profession)}>{profession.name}</a> : <span aria-current="page">{profession.name}</span>}
      {suffix && <><span aria-hidden="true">/</span><span aria-current="page">{childLabel(profession, suffix)}</span></>}
    </nav>
  )
}

function SectionHead({ eyebrow, title, body, id }: { eyebrow: string; title: string; body?: string; id?: string }) {
  return (
    <div className="ocp-section-head">
      <p className="ocp-eyebrow">{eyebrow}</p>
      <h2 id={id}>{title}</h2>
      {body && <p>{body}</p>}
    </div>
  )
}

function FeatureCards({ profession, image, workNoun, condensed = false }: { profession: Profession; image: ClusterImage; workNoun: string; condensed?: boolean }) {
  const role = roleName(profession)
  const leadsVisual = intentScene(image, 'leads', 2)
  const bookingVisual = intentScene(image, 'booking', 0)
  const cards = [
    {
      label: 'Calendar',
      text: `See your ${workNoun}, locations and the next part of the day in one diary.`,
      slug: 'admin-overload',
      image: productVisuals.calendar,
      alt: 'Offlico weekly calendar shown on a phone',
      visualType: 'product',
      icon: CalendarDays,
    },
    {
      label: 'Your own enquiries',
      text: `Use your own channels for ${role} enquiries, then organise suitable accepted work in Offlico. Local customer requests are not currently available for this role.`,
      slug: 'lead-generation',
      image: leadsVisual.src,
      alt: leadsVisual.alt,
      visualType: 'scene',
      icon: MapPin,
    },
    {
      label: 'Online booking',
      text: `Let people request ${role} work without a chain of calls and messages.`,
      slug: 'online-booking',
      image: bookingVisual.src,
      alt: bookingVisual.alt,
      visualType: 'scene',
      icon: Smartphone,
    },
    {
      label: 'Client records',
      text: `Find contacts, previous ${roleWork(role, workNoun)} and everyday business notes together.`,
      slug: 'client-records',
      image: productVisuals.records,
      alt: 'Offlico client contact and address details shown on a phone',
      visualType: 'product',
      icon: UsersRound,
    },
    {
      label: 'Automatic reminders',
      text: 'Remind people about agreed work before an avoidable gap in the day.',
      slug: 'appointment-reminders',
      image: productVisuals.reminders,
      alt: 'Offlico reminder responses shown on a laptop',
      visualType: 'product',
      icon: Clock3,
    },
    {
      label: 'Tap to Pay',
      text: 'Take a contactless payment on a supported phone without a separate card reader.',
      slug: '',
      image: productVisuals.payment,
      alt: 'Tap to Pay contactless payment screen on an iPhone',
      visualType: 'product',
      icon: CreditCard,
    },
  ]
  return (
    <section className={`ocp-features${condensed ? ' ocp-features-condensed' : ''}`} aria-labelledby="ocp-features-heading">
      <div className="ocp-container">
        <SectionHead id="ocp-features-heading" eyebrow="Features" title="Keep the business side moving" body={`Keep the practical business side of ${role} work connected, from the next booking through to payment.`} />
        <div className="ocp-feature-grid">
          {cards.map((card) => {
            const Icon = card.icon
            return (
              <a key={card.label} href={card.slug ? professionRoute(profession, card.slug) : '/features/tap-to-pay/'} className="ocp-feature-card">
                <div className={`ocp-feature-image ocp-feature-image-${card.visualType}`}><img src={card.image} alt={card.alt} loading="lazy" decoding="async" /></div>
                <div className="ocp-feature-copy"><Icon size={22} aria-hidden="true" /><h3>{card.label}</h3><p>{card.text}</p><span>Explore <ArrowRight size={16} aria-hidden="true" /></span></div>
              </a>
            )
          })}
        </div>
        <div className="ocp-feature-actions"><TrialAction /><a className="ocp-text-link" href="/features/">Explore all features <ArrowRight size={16} aria-hidden="true" /></a></div>
      </div>
    </section>
  )
}

function WorkdayProof({ profession, before, after }: { profession: Profession; before: string; after: string }) {
  const role = roleName(profession)
  return (
    <section className="ocp-workday" aria-labelledby="ocp-workday-heading">
      <div className="ocp-container ocp-workday-layout">
        <div>
          <p className="ocp-eyebrow">The practical difference</p>
          <h2 id="ocp-workday-heading">One booking. Every part connected.</h2>
          <p>{`Booked ${role} work is more than a time slot. The contact, location, agreed work and follow-up need to stay together.`}</p>
          <a className="ocp-text-link" href={professionRoute(profession, 'practice-management-software')}>See the complete workflow <ArrowRight size={16} aria-hidden="true" /></a>
        </div>
        <div className="ocp-workday-steps">
          <div><span>01</span><h3>Plan the work</h3><p>{before}</p></div>
          <div><span>02</span><h3>Keep the right contact</h3><p>Keep the booking, location and ordinary business contact details where you can find them.</p></div>
          <div><span>03</span><h3>Finish and follow up</h3><p>{after}</p></div>
        </div>
      </div>
    </section>
  )
}

const widerFeatures = [
  ['Calendar and scheduling', 'calendar'],
  ['Smart routing', 'smart-routing'],
  ['Online booking', 'online-booking-page'],
  ['Client records', 'client-records'],
  ['Automated reminders', 'reminders'],
  ['Services and prices', 'services-and-prices'],
  ['Invoices and receipts', 'invoices-and-receipts'],
  ['Payments', 'payments'],
  ['Tap to Pay', 'tap-to-pay'],
  ['Mileage tracking', 'mileage-tracking'],
  ['Bookkeeping', 'bookkeeping'],
  ['Documents and notes', 'documents-and-notes'],
  ['Reports and insights', 'reports'],
  ['Offline mode', 'offline-mode'],
  ['Stock management', 'stock'],
  ['Team access', 'team'],
] as const

function Explorer({ profession, current = '', fullToolkit = false }: { profession: Profession; current?: string; fullToolkit?: boolean }) {
  return (
    <section className="ocp-explorer" aria-labelledby="ocp-explorer-heading">
      <div className="ocp-container">
        <SectionHead id="ocp-explorer-heading" eyebrow="Explore the details" title="Find the part you need" />
        <div className="ocp-explorer-links">
          {current && <a href={professionRoute(profession)}>{profession.name} overview<ArrowRight size={16} aria-hidden="true" /></a>}
          {Object.keys(childLabels).filter((slug) => slug !== current).map((slug) => (
            <a key={slug} href={professionRoute(profession, slug)}>{childLabel(profession, slug)}<ArrowRight size={16} aria-hidden="true" /></a>
          ))}
        </div>
        {fullToolkit && <div className="ocp-explorer-more">
          <h3>Explore the wider Offlico toolkit</h3>
          <p>See the general features and choose the parts that fit your work.</p>
          <div className="ocp-explorer-links">
            {widerFeatures.map(([label, slug]) => <a key={slug} href={`/features/${slug}/`}>{label}<ArrowRight size={16} aria-hidden="true" /></a>)}
          </div>
        </div>}
      </div>
    </section>
  )
}

function Faq({ profession, suffix, fitNote }: { profession: Profession; suffix: string; fitNote: string }) {
  const role = roleName(profession)
  const intentQuestions: Record<string, { q: string; a: string }> = {
    'admin-overload': { q: `What should I keep beside a booking for ${role} work?`, a: 'Start with the agreed service, time, location and contact. If the plan changes, update those practical details so you are not relying on an old message when the next working day begins.' },
    'lead-generation': { q: 'What if an enquiry is outside my services or work area?', a: 'You decide which enquiries from your own channels to follow up. A clear answer about what you can do is better than accepting work that will not fit your service, route or diary.' },
    'online-booking': { q: `Does every request for ${role} work fit a standard time slot?`, a: 'Not necessarily. If the work needs a quote, conversation or site details, use the first request to agree that next step before promising the full scope of work.' },
    'appointment-reminders': { q: 'What can a booking reminder help with?', a: 'It gives both sides another chance to recognise the agreed time before work starts. Agree any access, preparation or location details when arranging the booking; a reminder cannot work those out for you.' },
    'client-records': { q: 'What belongs in an ordinary business record?', a: 'Keep the contact, address, booking history, useful logistics and payment follow-up together. If your work requires specialist case notes, keep those in the appropriate system.' },
    'practice-management-software': { q: 'Can I use Offlico alongside other work tools?', a: 'Yes. Try Offlico for the everyday diary, contact, reminder and payment steps, then decide which parts of that business workflow help you most. Keep any specialist work in the tools it needs.' },
  }
  const questions = suffix === '' ? [
    { q: `Is this useful for ${role} work away from a fixed desk?`, a: 'Offlico keeps ordinary booking, location, contact and payment details available around a mobile working day.' },
    { q: 'Does Offlico replace specialist tools?', a: fitNote },
  ] : suffix === 'lead-generation' ? [
    { q: `Are Offlico local customer requests available for ${role} work?`, a: 'No. They are not currently available for this profession. Use your own website, phone or other enquiry channels, then organise suitable accepted work in Offlico.' },
    intentQuestions[suffix],
  ] : suffix === 'client-records' || suffix === 'practice-management-software' ? [
    { q: `Can Offlico replace every tool I use for ${role} work?`, a: fitNote },
    intentQuestions[suffix],
  ] : [
    { q: `Is this useful for ${role} work away from a fixed desk?`, a: 'Offlico keeps ordinary booking, location, contact and payment details available around a mobile working day.' },
    intentQuestions[suffix],
  ]
  return (
    <section className="ocp-faq" aria-labelledby="ocp-faq-heading">
      <div className="ocp-container">
        <SectionHead id="ocp-faq-heading" eyebrow="Frequently asked questions" title="A few things worth knowing" />
        <div className="ocp-faq-grid">
          {questions.map((item) => <details key={item.q}><summary>{item.q}<ArrowRight size={18} aria-hidden="true" /></summary><p>{item.a}</p></details>)}
          <details><summary>Can I try Offlico before committing?<ArrowRight size={18} aria-hidden="true" /></summary><p>Start with a free trial and see how the everyday workflow fits your own way of working.</p></details>
        </div>
      </div>
    </section>
  )
}

function FinalCta({ profession, image }: { profession: Profession; image: ClusterImage }) {
  return (
    <section className="ocp-final" aria-labelledby="ocp-final-heading">
      <img className="ocp-final-scene" src={image.gallery?.[2]?.src || image.src} alt="" loading="lazy" decoding="async" />
      <div className="ocp-container ocp-final-layout">
        <div><p className="ocp-eyebrow">Offlico for {roleName(profession)} work</p><h2 id="ocp-final-heading">Make the next working day easier to run.</h2><p>Bring your diary, contacts, reminders, payments and ordinary admin into one place.</p></div>
        <div><TrialAction /><TrialFinePrint tone="dark" /></div>
      </div>
    </section>
  )
}

function HubPage({ profession, image, nuance, before, after, setting, fitNote, workNoun }: { profession: Profession; image: ClusterImage; nuance: string; before: string; after: string; setting: string; fitNote: string; workNoun: string }) {
  const role = roleName(profession)
  const gallery = image.gallery?.slice(0, 3) || []
  return (
    <>
      <section className="ocp-hub-hero" aria-labelledby="ocp-title">
        <div className="ocp-container ocp-hub-hero-layout">
          <div className="ocp-hub-copy">
            <PageBreadcrumb profession={profession} suffix="" />
            <h1 id="ocp-title">{professionPageHeading(profession)}</h1>
            <p>{`Keep your ${workNoun} and the ordinary business details of ${role} work in one place: contacts, locations, reminders and payments.`}</p>
            <TrialAction /><TrialFinePrint />
          </div>
          <div className={`ocp-hero-gallery${gallery.length ? ' ocp-hero-gallery-custom' : ''}`} aria-label={`Images for ${role} work and Offlico`}>
            {gallery.length ? gallery.map((visual, index) => (
              <figure key={visual.src + index}><img src={visual.src} alt={visual.alt} loading="eager" decoding="async" /></figure>
            )) : (
              <>
                <figure><img src={image.src} alt={image.alt} loading="eager" decoding="async" /></figure>
                <figure className="ocp-gallery-product"><img src={productVisuals.calendar} alt="Offlico calendar showing a working day" loading="eager" decoding="async" /></figure>
                <figure className="ocp-gallery-product"><img src={productVisuals.records} alt="Offlico client contact details shown on a phone" loading="eager" decoding="async" /></figure>
              </>
            )}
          </div>
        </div>
      </section>
      <FeatureCards profession={profession} image={image} workNoun={workNoun} />
      <section className="ocp-context" aria-labelledby="ocp-context-heading"><div className="ocp-container ocp-context-layout"><div><p className="ocp-eyebrow">Made for work on the move</p><h2 id="ocp-context-heading">The work is specific. The admin should be simple.</h2><p>{nuance}</p></div><div className="ocp-context-list"><p><MapPin size={22} aria-hidden="true" />{setting}</p><p><CalendarDays size={22} aria-hidden="true" />{before}</p><p><Check size={22} aria-hidden="true" />{after}</p></div></div></section>
      <WorkdayProof profession={profession} before={before} after={after} />
      <Explorer profession={profession} fullToolkit />
      <Faq profession={profession} suffix="" fitNote={fitNote} />
      <FinalCta profession={profession} image={image} />
    </>
  )
}

const childHeroImageIndex: Record<string, number> = {
  'admin-overload': 0,
  'lead-generation': 2,
  'online-booking': 0,
  'appointment-reminders': 2,
  'client-records': 1,
  'practice-management-software': 0,
}
const childHeroImageKind: Record<string, string> = {
  'admin-overload': 'admin-hero',
  'lead-generation': 'leads-hero',
  'online-booking': 'booking-hero',
  'appointment-reminders': 'reminders-hero',
  'client-records': 'records-hero',
  'practice-management-software': 'records-hero',
}
const childSupportImageKind: Record<string, string> = {
  'admin-overload': 'admin-support',
  'lead-generation': 'leads-support',
  'online-booking': 'booking-support',
  'appointment-reminders': 'reminders-support',
  'client-records': 'records-support',
  'practice-management-software': 'records-support',
}

function ChildVisual({ image, slug }: { image: ClusterImage; slug: string }) {
  const visual = image.scenes?.[childHeroImageKind[slug]] || image.gallery?.[childHeroImageIndex[slug]]
  return (
    <div className="ocp-child-visual">
      <img className="ocp-child-scene" src={visual?.src || image.src} alt={visual?.alt || image.alt} loading="eager" decoding="async" />
    </div>
  )
}

function ProductWorkflow({ profession, slug, image, before, after }: { profession: Profession; slug: string; image: ClusterImage; before: string; after: string }) {
  const editorialVisual = slug === 'lead-generation' ? intentScene(image, 'leads', 2)
    : slug === 'online-booking' ? intentScene(image, 'booking', 0)
      : null
  const visual = editorialVisual || (slug === 'appointment-reminders'
    ? { src: productVisuals.reminders, alt: 'Offlico reminder responses shown on a laptop' }
    : slug === 'client-records'
      ? { src: productVisuals.records, alt: 'Offlico client contact and address details shown on a phone' }
      : { src: productVisuals.calendar, alt: 'Offlico weekly calendar shown on a phone' })
  const copy: Record<string, string> = {
    'admin-overload': 'See the next booking, its location and the contact details before the day starts.',
    'lead-generation': 'Offlico local customer requests are not currently available for this profession. Take enquiries through your existing channels, then add suitable accepted work to your Offlico diary.',
    'online-booking': 'Make the service and next available time clear while you focus on the work in front of you.',
    'appointment-reminders': 'Keep the booking and its automatic reminder in the same everyday workflow.',
    'client-records': 'Find the next booking alongside the contact and previous ordinary business notes.',
    'practice-management-software': 'Bring the calendar, contacts and payment steps of a working day together.',
  }
  const headings: Record<string, string> = {
    'admin-overload': 'A clearer view of the next step.',
    'lead-generation': 'From enquiry to booked work.',
    'online-booking': 'A clearer way to start a booking.',
    'appointment-reminders': 'A clearer view of the next step.',
    'client-records': 'A clearer view of the next step.',
    'practice-management-software': 'A clearer view of the next step.',
  }
  return (
    <section className="ocp-product-workflow" aria-labelledby="ocp-product-workflow-heading">
      <div className="ocp-container ocp-product-workflow-layout">
        <div className={`ocp-product-workflow-visual${editorialVisual ? ' ocp-product-workflow-visual-scene' : ''}`}><img src={visual.src} alt={visual.alt} loading="lazy" decoding="async" /></div>
        <div><p className="ocp-eyebrow">{editorialVisual ? 'The working situation' : 'Offlico in practice'}</p><h2 id="ocp-product-workflow-heading">{headings[slug]}</h2><p>{copy[slug]}</p><p>{slug === 'client-records' ? after : before}</p><p>{childOutcomeCopy[slug]}</p>{slug !== 'practice-management-software' && <a className="ocp-text-link" href={professionRoute(profession, 'practice-management-software')}>Explore the full workflow <ArrowRight size={16} aria-hidden="true" /></a>}</div>
      </div>
    </section>
  )
}

function ChildPage({ profession, slug, image, nuance, before, after, fitNote, workNoun, bookingPattern }: { profession: Profession; slug: string; image: ClusterImage; nuance: string; before: string; after: string; fitNote: string; workNoun: string; bookingPattern: string }) {
  const role = roleName(profession)
  const benefit = childBenefitContent[slug]
  const intro = childIntros[slug](role, workNoun)
  const steps = slug === 'admin-overload' ? [
    { title: 'Plan from one diary', body: before, icon: CalendarDays },
    { title: 'Keep details together', body: `See the contact, location and previous ${roleWork(role, workNoun)} beside the next booking.`, icon: UsersRound },
    { title: 'Close the loop', body: after, icon: Check },
  ] : slug === 'lead-generation' ? [
    { title: 'Review your enquiry', body: `Check what ${role} work is needed and whether it suits your services.`, icon: MessageSquare },
    { title: 'Check the place and time', body: 'Compare the requested location with your existing work, travel and available diary space.', icon: MapPin },
    { title: 'Follow up clearly', body: 'Contact the enquirer when the work is a fit, then keep the agreed details together.', icon: CalendarDays },
  ] : slug === 'online-booking' ? [
    { title: 'Show what can be booked', body: `Set out the ${role} service and the information someone needs before booking.`, icon: ClipboardList },
    { title: 'Collect useful details', body: 'Bring the requested time, location and contact into the booking workflow.', icon: MapPin },
    { title: 'Keep the day connected', body: before, icon: CalendarDays },
  ] : slug === 'appointment-reminders' ? [
    { title: 'Book the work', body: before, icon: CalendarDays },
    { title: 'Send the reminder', body: 'Let Offlico send an automatic booking reminder before the agreed time.', icon: MessageSquare },
    { title: 'Start prepared', body: 'Check the contact, destination and agreed work before starting.', icon: MapPin },
  ] : slug === 'client-records' ? [
    { title: 'One contact profile', body: 'Keep the right contact, address and ordinary business details together.', icon: UsersRound },
    { title: 'See the booking history', body: `Find previous ${roleWork(role, workNoun)} and the next booking without searching across messages.`, icon: CalendarDays },
    { title: 'Finish the admin', body: after, icon: FileText },
  ] : [
    { title: 'Plan the work', body: before, icon: CalendarDays },
    { title: 'Keep the right contact', body: `Keep ${role} booking and contact details available during the working day.`, icon: UsersRound },
    { title: 'Finish and follow up', body: after, icon: CreditCard },
  ]
  return (
    <>
      <section className="ocp-child-hero" aria-labelledby="ocp-title">
        <ChildVisual image={image} slug={slug} />
        <div className="ocp-child-hero-mask" aria-hidden="true"><svg viewBox="0 0 1000 1000" preserveAspectRatio="none" focusable="false"><path d="M0 0H820C790 0 778 19 764 56L642 388C604 496 598 625 590 744C582 866 568 956 560 1000H0Z" /></svg></div>
        <div className="ocp-container"><div className="ocp-child-hero-layout"><div className="ocp-child-hero-copy">{slug !== 'admin-overload' && <PageBreadcrumb profession={profession} suffix={slug} />}<p className="ocp-eyebrow">For {rolePlural(profession)}</p><h1 id="ocp-title">{slug === 'practice-management-software' && usesPracticeManagementLanguage(profession) ? `Practice management for ${rolePlural(profession)}` : childHeadings[slug](rolePlural(profession))}</h1><p>{intro}</p><div className="ocp-hero-actions"><TrialAction /><a className="ocp-secondary" href="#ocp-how-it-works">See how it works</a></div><TrialFinePrint /></div></div></div>
      </section>
      <section className="ocp-child-intro" id="ocp-how-it-works"><div className="ocp-container ocp-child-intro-layout"><div><p className="ocp-eyebrow">{childEyebrows[slug]}</p><h2>{childIntroHeadings[slug]}</h2><p>{nuance}</p><p>{childPracticalCopy(slug, role, bookingPattern)}</p></div><div className="ocp-intro-photo"><img src={image.scenes?.[childSupportImageKind[slug]]?.src || image.gallery?.[slug === 'client-records' ? 2 : 1]?.src || image.src} alt={image.scenes?.[childSupportImageKind[slug]]?.alt || image.gallery?.[slug === 'client-records' ? 2 : 1]?.alt || image.alt} loading="lazy" decoding="async" /></div></div></section>
      <ProductWorkflow profession={profession} slug={slug} image={image} before={before} after={after} />
      <section className="ocp-child-steps"><div className="ocp-container"><SectionHead eyebrow="How it works" title={childStepHeadings[slug]} body={`A practical way to manage the business side of ${role} work.`} /><div className="ocp-step-grid">{steps.map((step, index) => { const Icon = step.icon; return <article key={step.title}><span className="ocp-step-number">0{index + 1}</span><Icon size={28} aria-hidden="true" /><h3>{step.title}</h3><p>{step.body}</p></article> })}</div></div></section>
      {slug === 'practice-management-software' ? <FeatureCards profession={profession} image={image} workNoun={workNoun} condensed /> : (
        <section className="ocp-child-benefits"><div className="ocp-container ocp-child-benefits-layout"><div><p className="ocp-eyebrow">What changes</p><h2>{benefit.heading}</h2><p>{benefit.body(role, workNoun)}</p><TrialAction /></div><div className="ocp-benefit-list">{benefit.points.map((point) => <p key={point}><Check />{point}</p>)}</div></div></section>
      )}
      {(slug === 'client-records' || slug === 'practice-management-software') && <section className="ocp-fit-note"><div className="ocp-container"><h2>Where specialist tools still matter</h2><p>{fitNote}</p></div></section>}
      <Explorer profession={profession} current={slug} />
      <Faq profession={profession} suffix={slug} fitNote={fitNote} />
      <FinalCta profession={profession} image={image} />
    </>
  )
}

export default function PageProfessionCluster({ profession, slug = '' }: { profession: Profession; slug?: string }) {
  const route = slug
    ? PROFESSION_CHILD_ROUTE_META.find((entry) => entry.profession.id === profession.id && entry.featureSlug === slug)
    : PROFESSION_ROUTE_META.find((entry) => entry.profession.id === profession.id)
  useMeta(route?.title || `Offlico for ${profession.name}`, route?.description || '')
  const content = getProfessionCategoryContent(profession.categorySlug)
  const image = getProfessionHeroImage(profession) as ClusterImage
  const nuance = getProfessionNuance(profession.id) || content?.intro || `Keep the everyday business side of ${roleName(profession)} work organised.`
  const workflow = getProfessionWorkflow(profession.id)
  const presentation = getProfessionPresentation(profession)
  const workNoun = professionWorkNounFor(profession)
  const before = workflow?.before || content?.before || 'Check the booking, contact and location before starting the work.'
  const after = workflow?.after || content?.after || 'Keep the next step and payment follow-up in view.'
  const setting = presentation.serviceSetting || 'customer locations'
  const fitNote = content?.fitNote || 'Check any specialist professional, safety or regulatory requirements for your own work before choosing software.'
  return (
    <div className="chiropodist-page ocp-cluster">
      <SharedNav active="app" />
      <main>{slug ? <ChildPage profession={profession} slug={slug} image={image} nuance={nuance} before={before} after={after} fitNote={fitNote} workNoun={workNoun} bookingPattern={presentation.bookingPattern} /> : <HubPage profession={profession} image={image} nuance={nuance} before={before} after={after} setting={setting} fitNote={fitNote} workNoun={workNoun} />}</main>
      <SharedFooter />
    </div>
  )
}
